// Patch 29/31 · home "Nível de loja": every store with at least one Apoio ⚡ this season (scrolls after 5),
// a search for any store, the support streak (folded) and the rewards in a pop-up.
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronDown, Search, X } from "lucide-react";
import { api, resolveImageUrl } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { entryPath } from "@/lib/paths";
import { entryImage } from "@/lib/placeholders";
import { Skel } from "@/components/home/HomeStrips";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Bolt, PixelText, RANK_C, Segs, SlotNum, endsText, giveSupport, rewardText, seasonNum } from "@/components/season/bits";

const LADDER = [1, 3, 5, 7, 30];
const canHover = () => typeof window !== "undefined" && window.matchMedia?.("(hover: hover)").matches;
const fold = (s) => (s || "").normalize("NFKD").replace(/[̀-ͯ]/g, "").toLowerCase();

function Streak({ me, H, user }) {
  const [open, setOpen] = useState(false);
  const n = me?.streak || 0;
  const today = me?.today?.length || 0;
  return (
    <div className="border border-amber-400/60 bg-amber-400/[0.05]" data-testid="support-streak">
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} className="w-full flex items-center gap-2 px-3 py-2.5 text-[14px] font-bold text-stone-100 text-left" data-testid="streak-toggle">
        <Bolt size={15} />
        <span className="min-w-0 truncate">{H.streak}: <span className="text-amber-300">{n === 1 ? H.day1 : H.days.replace("{n}", n)}</span></span>
        {user && <span className="ml-auto text-[12px] font-medium text-stone-400 tabular-nums whitespace-nowrap">{H.today.replace("{n}", today).replace("{m}", me?.max || 3)}</span>}
        <ChevronDown className={`w-4 h-4 text-amber-300 shrink-0 transition-transform ${user ? "" : "ml-auto"} ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-3 pb-3 tier-pop-soft" data-testid="streak-ladder">
          <div className="grid grid-cols-5 gap-1.5">
            {LADDER.map((d) => {
              const got = n >= d;
              const next = !got && LADDER.find((x) => x > n) === d;
              return (
                <div key={d} className={`text-center px-1 py-1.5 border text-[11px] leading-tight ${got ? "border-amber-400 bg-amber-400/15 text-amber-200" : next ? "border-emerald-400/70 text-emerald-300" : "border-white/10 text-stone-400"}`}>
                  <div className="font-bold">{got ? "✓" : next ? <Bolt size={10} color="#6EE7B7" /> : "★"} D{d}</div>
                  <div className="mt-0.5">{H.ladder[d]}</div>
                </div>
              );
            })}
          </div>
          {!user && <Link to="/register" className="block mt-2 text-[13px] text-amber-300 hover:text-amber-200">{H.sign_streak} →</Link>}
        </div>
      )}
    </div>
  );
}

function RewardsDialog({ open, onClose, H }) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md bg-[#16161b] border border-amber-500/30 text-stone-100" data-testid="support-rewards">
        <DialogTitle className="font-display text-[28px] font-black uppercase leading-none text-amber-300">{H.rewards}</DialogTitle>
        <DialogDescription className="sr-only">{H.hint}</DialogDescription>
        <ul className="divide-y divide-white/5 -mt-1">
          {H.rw.slice(0, 3).map(([a, b], i) => (
            <li key={i} className="flex gap-3 py-2.5">
              <span className="w-7 h-7 shrink-0 grid place-items-center border border-white/10">{i === 0 ? <Bolt size={13} /> : <span className="text-amber-300 text-[12px]">{["", "≡", "★", "◆", "▲"][i]}</span>}</span>
              <span className="min-w-0">
                <span className="block text-[14px] font-bold text-stone-100">{a}</span>
                <span className="block text-[12.5px] text-stone-400 leading-snug">{b}</span>
              </span>
            </li>
          ))}
        </ul>
        <div className="flex items-center justify-between gap-3 text-[13px]">
          <Link to="/hall-da-fama" onClick={onClose} className="text-amber-300 hover:text-amber-200" data-testid="hof-link">{H.hof_link}</Link>
          <Link to="/funcionalidades#recompensas" onClick={onClose} className="font-semibold text-amber-300 hover:text-amber-200" data-testid="rewards-all">{H.see_all} →</Link>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Patch 33: desktop right column (mobile: under the list) — latest champion (patch 38: scepter moved out).
function HofCard({ H, t, S, lang }) {
  const [hof, setHof] = useState(null);
  useEffect(() => { api.get("/hall-of-fame").then(({ data }) => setHof(data)).catch(() => setHof({ seasons: [] })); }, []);
  const last = hof?.seasons?.[0];
  const champ = last?.podium?.[0];
  const ends = S?.ends_at ? new Date(new Date(S.ends_at).getTime() - 86400000).toLocaleDateString(lang === "en" ? "en-GB" : "pt-PT", { day: "numeric", month: "long" }) : "";
  return (
    <aside className="border border-amber-400/40 bg-gradient-to-b from-amber-400/[0.07] to-transparent" data-testid="hof-card">
      <div className="px-4 pt-4 pb-3">
        <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-amber-400">{H.hof_title}</div>
        {!hof && <Skel className="h-[72px] w-full mt-3" />}
        {hof && champ && (
          <Link to={entryPath({ id: champ.entry_id, slug: champ.slug, category: "stores" })} className="mt-3 flex items-center gap-3 group" data-testid="hof-card-champ">
            <span className="relative shrink-0">
              <span className="hof-frame block rounded-[8px]">
                <img src={resolveImageUrl(entryImage({ ...champ, category: "stores" }), 96)} alt="" className="block w-16 h-16 object-cover rounded-[5px] bg-[#1b1c22]" loading="lazy" />
              </span>
              <span className="absolute -top-2.5 -left-2.5"><SlotNum n={1} size={32} /></span>
            </span>
            <span className="min-w-0">
              <span className="block text-[12px] uppercase tracking-[0.14em] font-bold text-stone-400">{H.hof_card_champ.replace("{s}", H.season.replace("{n}", seasonNum(last)))}</span>
              <span className="block font-display text-[24px] font-black uppercase leading-[0.95] text-stone-100 group-hover:text-amber-200 truncate">{champ.name}</span>
              <span className="text-[12.5px] text-amber-300 inline-flex items-center gap-1"><Bolt size={10} />{champ.xp} XP</span>
            </span>
          </Link>
        )}
        {hof && !champ && <p className="mt-2 text-[14px] text-stone-200 leading-snug" data-testid="hof-card-first">{H.hof_card_first.replace("{d}", ends)}</p>}
        <Link to="/hall-da-fama" className="inline-block mt-3 text-[13px] font-semibold text-amber-300 hover:text-amber-200" data-testid="hof-card-link">{H.hof_link}</Link>
      </div>
    </aside>
  );
}

function Row({ r, i, H, top, done, sel, onSelect, onSupport, busy, plus, fresh }) {
  const color = RANK_C[Math.min(3, i)];
  const fill = fresh ? 0 : top ? (r.value / top) * 20 : 0;
  const sub = fresh ? H.no_support_yet
    : i === 0 ? (r.days_top ? H.top_max.replace("{n}", r.days_top) : H.top_max0)
      : r.gap > 0 ? H.gap_season.replace("{n}", r.gap).replace("{r}", i) : "";
  return (
    <div data-row={r.id} className={`lvl-row ${i === 0 && !fresh ? "is-top" : ""} ${sel ? "is-sel" : ""}`} style={{ "--c": color }} data-testid={`lvl-row-${r.id}`}>
      <button type="button" className="flex items-center gap-3 min-w-0 flex-1 text-left" onClick={() => onSelect(r)} aria-label={r.name}>
        {fresh ? <img src={resolveImageUrl(entryImage(r), 96)} alt="" className="w-10 h-10 m-1 object-cover rounded-sm bg-[#1b1c22] shrink-0" loading="lazy" /> : <SlotNum n={i + 1} />}
        <span className="min-w-0 flex-1">
          <span className="flex items-baseline justify-between gap-2">
            <span className="font-bold text-[15px] leading-tight text-stone-100 truncate">{r.name}</span>
            {!fresh && <span className="text-[12px] font-bold whitespace-nowrap tabular-nums inline-flex items-center text-amber-300"><Bolt size={11} className="mr-0.5" />{r.value} XP</span>}
          </span>
          {!fresh && <Segs fill={fill} color={color} glow={i === 0} />}
          {sub && <span className={`block text-[12px] mt-1 truncate ${fresh ? "text-emerald-300" : "text-stone-400"}`}>{sub}</span>}
          {sel && (
            <span className="lvl-info" data-testid="lvl-info">
              <img src={resolveImageUrl(entryImage(r), 96)} alt="" className="w-7 h-7 object-cover rounded-sm bg-[#1b1c22]" loading="lazy" />
              <span className="flex-1 min-w-0 truncate text-stone-300">{r.city}{r.week ? ` · ${H.week.replace("{n}", r.week)}` : ""}</span>
              <span className="bg-amber-400 text-[#0A0A0C] font-bold uppercase text-[11px] tracking-[0.08em] px-2.5 py-1.5">{H.see_store}</span>
            </span>
          )}
        </span>
      </button>
      <button type="button" onClick={() => onSupport(r)} disabled={busy} aria-pressed={done} aria-label={done ? H.sb_done_title : H.sb_btn}
        className={`apb ${done ? "done" : ""}`} data-testid={`lvl-support-${r.id}`}>
        <Bolt size={18} color={done ? "#6EE7B7" : "#F5C518"} />
        {plus && <span className="xp-plus"><PixelText /></span>}
      </button>
    </div>
  );
}

export default function StoreLevel() {
  const { t, lang } = useI18n();
  const H = t.h29;
  const { user } = useAuth();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [plus, setPlus] = useState(null);
  const [showRw, setShowRw] = useState(false);
  const [searchOn, setSearchOn] = useState(false);
  const [q, setQ] = useState("");
  const [found, setFound] = useState([]);
  const [fade, setFade] = useState(false);
  const list = useRef(null);
  const pos = useRef({});
  const input = useRef(null);

  const load = useCallback(() => api.get("/seasons/board?mode=season").then(({ data }) => setD(data)).catch(() => setD((x) => x || { rows: [], me: null })), []);
  useEffect(() => { load(); }, [user?.id, load]);
  useEffect(() => {   // live: every 30 s while the tab is visible
    const id = setInterval(() => document.visibilityState === "visible" && load(), 30000);
    return () => clearInterval(id);
  }, [load]);

  // search: filter the season list, and also find stores that have no Apoio yet
  useEffect(() => {
    if (!searchOn || q.trim().length < 2) { setFound([]); return undefined; }
    const id = setTimeout(() => {
      api.get("/search", { params: { q: q.trim(), limit: 12 } })
        .then(({ data }) => setFound((data.results || []).filter((e) => e.category === "stores")))
        .catch(() => setFound([]));
    }, 250);
    return () => clearTimeout(id);
  }, [q, searchOn]);
  useEffect(() => { if (searchOn) input.current?.focus(); }, [searchOn]);

  // rows slide into their new place when a store overtakes another (FLIP)
  useLayoutEffect(() => {
    const el = list.current;
    if (!el) return;
    const now = {};
    el.querySelectorAll("[data-row]").forEach((n) => { now[n.dataset.row] = n.getBoundingClientRect().top + el.scrollTop; });
    Object.entries(now).forEach(([id, top]) => {
      const before = pos.current[id];
      const n = el.querySelector(`[data-row="${id}"]`);
      if (before != null && before !== top && n?.animate) n.animate([{ transform: `translateY(${before - top}px)` }, { transform: "none" }], { duration: 450, easing: "cubic-bezier(.2,.8,.2,1)" });
    });
    pos.current = now;
    setFade(el.scrollHeight > el.clientHeight + 4 && el.scrollTop + el.clientHeight < el.scrollHeight - 4);
  }, [d, q, found]);
  const onScroll = (e) => {
    const el = e.currentTarget;
    setFade(el.scrollTop + el.clientHeight < el.scrollHeight - 4);
  };

  const onSelect = (r) => {
    if (canHover() || sel === r.id) return nav(entryPath(r));
    setSel(r.id);
  };
  const today = new Set(d?.me?.today || []);
  const onSupport = async (r) => {
    if (!user) { toast(H.sb_login); return nav("/login"); }
    if (today.has(r.id)) return toast(H.err.already);
    setBusy(true);
    try {
      const res = await giveSupport(r.id, H);
      setPlus(r.id);
      setTimeout(() => setPlus(null), 1300);
      const extra = rewardText(res, H);
      toast.success(extra || H.sb_shard.replace("{n}", 3 - res.left));
      await load();
    } catch (e) {
      if (!e.silent) toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const all = d?.rows || [];
  const top = all[0]?.value || 0;
  const S = d?.season;
  const term = fold(q.trim());
  const searching = searchOn && term.length > 0;
  const rows = searching ? all.filter((r) => fold(`${r.name} ${r.city}`).includes(term)) : all;
  const inList = new Set(all.map((r) => r.id));
  const extra = searching ? found.filter((e) => !inList.has(e.id)) : [];
  const rankOf = (r) => all.findIndex((x) => x.id === r.id);
  return (
    <section id="nivel-de-loja" className="py-12 lg:py-16 border-t border-amber-500/10 scroll-mt-20" data-testid="store-level">
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 grid gap-6 lg:gap-10 lg:grid-cols-[minmax(0,680px)_minmax(0,1fr)] lg:items-start">
        <div className="min-w-0">
        <div className="text-center lg:text-left">
          <h2 className="font-display text-[34px] sm:text-5xl font-black uppercase leading-none text-stone-100 inline-flex items-center justify-center gap-3 sm:gap-4">
            {H.lvl_title}
            <span className="px-sprite px-storelevel" style={{ backgroundImage: "url(/pixo/store-level-sheet.png)" }} aria-hidden data-testid="store-level-icon" />
          </h2>
          <Link to="/funcionalidades#nivel-de-loja" className="block w-max mx-auto lg:mx-0 mt-2 text-[13px] font-semibold text-amber-300 hover:text-amber-200 underline-offset-2 hover:underline" data-testid="lvl-what">{H.what_is}</Link>
        </div>
        <div className="mt-5 flex items-center gap-2">
          <span className="bg-amber-400 text-[#0A0A0C] inline-flex items-center gap-1.5 rounded-full font-bold uppercase text-[12px] tracking-[0.1em] px-3.5 py-1.5" data-testid="season-pill">
            <Bolt size={11} color="#0A0A0C" />{S ? H.season.replace("{n}", seasonNum(S)) : "…"}
          </span>
          {S && <span className="text-[12.5px] font-semibold text-amber-300 whitespace-nowrap" data-testid="season-ends">{endsText(S, H)}</span>}
          <button type="button" onClick={() => { setSearchOn((v) => !v); setQ(""); }} aria-label={H.search} aria-expanded={searchOn}
            className={`ml-auto w-9 h-9 grid place-items-center border transition-colors ${searchOn ? "border-amber-400 text-amber-300 bg-amber-400/10" : "border-white/15 text-stone-300 hover:border-amber-400/60"}`} data-testid="lvl-search-btn">
            {searchOn ? <X className="w-4 h-4" /> : <Search className="w-4 h-4" />}
          </button>
        </div>
        {searchOn && (
          <div className="relative mt-2 tier-pop-soft">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-500" />
            <input ref={input} value={q} onChange={(e) => setQ(e.target.value)} placeholder={H.search_ph}
              className="w-full bg-[#121216] border border-amber-500/30 focus:border-amber-400 rounded-sm pl-9 pr-3 py-2.5 text-[15px] text-stone-100 placeholder:text-stone-500" data-testid="lvl-search" />
          </div>
        )}
        <div className="relative mt-3">
          <div ref={list} onScroll={onScroll} className="lvl-list space-y-2" onMouseLeave={() => canHover() && setSel(null)} data-testid="lvl-list">
            {!d && [0, 1, 2, 3].map((k) => <Skel key={k} className="h-[66px] w-full" />)}
            {d && !all.length && !searching && (
              <div className="border border-dashed border-amber-400/50 p-5 text-center text-[14px] text-stone-300" data-testid="lvl-empty">
                {H.empty}
                <Link to="/stores" className="block mt-2 text-amber-300 font-bold">{H.empty_cta} →</Link>
              </div>
            )}
            {rows.map((r) => {
              const i = rankOf(r);
              return <Row key={r.id} r={r} i={i} H={H} top={top} done={today.has(r.id)} sel={sel === r.id}
                onSelect={onSelect} onSupport={onSupport} busy={busy} plus={plus === r.id} />;
            })}
            {extra.map((r) => (
              <Row key={r.id} r={r} i={9} H={H} top={top} done={today.has(r.id)} sel={sel === r.id} fresh
                onSelect={onSelect} onSupport={onSupport} busy={busy} plus={plus === r.id} />
            ))}
            {searching && !rows.length && !extra.length && q.trim().length >= 2 && (
              <div className="text-center text-[14px] text-stone-400 py-4" data-testid="lvl-nothing">{H.search_none}</div>
            )}
          </div>
          {fade && <div className="lvl-fade" aria-hidden />}
        </div>
        <div className="mt-3"><Streak me={d?.me} H={H} user={user} /></div>
        <div className="flex flex-col items-center gap-2 mt-3">
          <span className="text-[13px] text-amber-300 text-center">{H.hint}</span>
          <button type="button" onClick={() => setShowRw(true)} className="inline-flex items-center gap-1.5 border border-amber-400/50 hover:border-amber-400 text-amber-200 text-[12px] font-bold uppercase tracking-[0.1em] px-3.5 py-2 rounded-full" data-testid="toggle-rewards">
            <Bolt size={11} /> {H.see_rewards}
          </button>
        </div>
        <RewardsDialog open={showRw} onClose={() => setShowRw(false)} H={H} />
        </div>
        {/* patch 38: the Hall of Fame card sits in the middle of the column (the scepter lives in "See rewards") */}
        <div className="lg:self-stretch lg:flex lg:items-center"><div className="w-full"><HofCard H={H} t={t} S={S} lang={lang} /></div></div>
      </div>
    </section>
  );
}
